import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  RotateCcw, 
  Calendar, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  X, 
  Save,
  Trash2,
  Edit,
  RefreshCw,
  Flag,
  Users
} from 'lucide-react';
import { doc, updateDoc, deleteDoc, collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const FixtureManager = ({ fixture, onUpdate, onClose }) => {
  const { isSuperAdmin } = useAuth();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [show4MatchDialog, setShow4MatchDialog] = useState(false);
  const [showWalkoverDialog, setShowWalkoverDialog] = useState(false);
  const [walkoverWinner, setWalkoverWinner] = useState('');
  const [showTeamSelectionDialog, setShowTeamSelectionDialog] = useState(false);
  const [selectedHomeTeam, setSelectedHomeTeam] = useState('');
  const [selectedAwayTeam, setSelectedAwayTeam] = useState('');
  const [availableTeams, setAvailableTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Reschedule form state
  const [newDate, setNewDate] = useState(fixture?.date || '');
  const [newTime, setNewTime] = useState(fixture?.time || '');
  const [newCourt, setNewCourt] = useState(fixture?.court || '');

  // Debug reschedule dialog state
  useEffect(() => {
    console.log('Reschedule dialog state changed:', showRescheduleDialog);
  }, [showRescheduleDialog]);

  // Load available teams for TBD fixture selection
  useEffect(() => {
    const loadTeams = async () => {
      try {
        // Get all teams (no leagueId filter as teams might not have that field)
        const teamsSnapshot = await getDocs(collection(db, 'teams'));
        const teamsData = teamsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('✅ Loaded teams for selection:', teamsData.length, teamsData.map(t => t.name));
        setAvailableTeams(teamsData);
      } catch (err) {
        console.error('❌ Error loading teams:', err);
      }
    };
    
    loadTeams();
  }, []);

  const resetFixture = async () => {
    console.log('🔄 RESET FIXTURE CALLED for fixture:', fixture.id);
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      console.log('Resetting fixture status and clearing all data...');
      
      // Reset fixture to scheduled status with initialized sets
      await updateDoc(doc(db, 'fixtures', fixture.id), {
        status: 'scheduled',
        sets: [
          { set: 1, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, homePoints: 0, awayPoints: 0, completed: false, inTiebreak: false },
          { set: 2, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, homePoints: 0, awayPoints: 0, completed: false, inTiebreak: false },
          { set: 3, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, homePoints: 0, awayPoints: 0, completed: false, inTiebreak: false }
        ],
        playerStats: null,
        currentSet: 1,
        deuceCount: 0,
        startedAt: null,
        completedAt: null,
        winner: null,
        finalScore: null,
        homeSetsWon: 0,
        awaySetsWon: 0,
        homePoints: 0,
        awayPoints: 0,
        updatedAt: new Date().toISOString()
      });
      
      console.log('✅ Fixture document updated');

      // Delete any existing match records for this fixture
      console.log('Searching for match records to delete...');
      const matchesQuery = query(
        collection(db, 'matches'),
        where('fixtureId', '==', fixture.id)
      );
      const matchesSnapshot = await getDocs(matchesQuery);
      console.log(`Found ${matchesSnapshot.docs.length} match records to delete`);
      
      if (matchesSnapshot.docs.length > 0) {
        const deletePromises = matchesSnapshot.docs.map(doc => {
          console.log('Deleting match record:', doc.id);
          return deleteDoc(doc.ref);
        });
        await Promise.all(deletePromises);
        console.log('✅ All match records deleted');
      }

      console.log('🎉 Fixture reset completed successfully!');
      setSuccess('Fixture reset successfully!');
      
      // Call onUpdate to refresh the fixtures list
      if (onUpdate) {
        console.log('Calling onUpdate to refresh fixtures list...');
        onUpdate();
      }
      
      setTimeout(() => {
        setShowResetDialog(false);
        setSuccess('');
        onClose(); // Close the entire manager modal
      }, 1500);
      
    } catch (err) {
      console.error('❌ Error resetting fixture:', err);
      console.error('Error details:', err.message, err.stack);
      setError('Failed to reset fixture: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const rescheduleFixture = async () => {
    setLoading(true);
    setError('');
    try {
      await updateDoc(doc(db, 'fixtures', fixture.id), {
        date: newDate,
        time: newTime,
        court: newCourt,
        status: 'scheduled',
        updatedAt: new Date().toISOString()
      });

      setSuccess('Fixture rescheduled successfully!');
      onUpdate();
      setTimeout(() => {
        setShowRescheduleDialog(false);
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError('Failed to reschedule fixture: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const create4Matches = async () => {
    console.log('🟢 CREATE 4 MATCHES CALLED');
    console.log('Fixture data:', fixture);
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      console.log('Searching for teams...');
      console.log('Home Team:', fixture.homeTeam);
      console.log('Away Team:', fixture.awayTeam);
      
      // Get teams and their players
      const [homeTeamDoc, awayTeamDoc] = await Promise.all([
        getDocs(query(collection(db, 'teams'), where('name', '==', fixture.homeTeam))),
        getDocs(query(collection(db, 'teams'), where('name', '==', fixture.awayTeam)))
      ]);

      console.log('Home team found:', !homeTeamDoc.empty);
      console.log('Away team found:', !awayTeamDoc.empty);

      if (homeTeamDoc.empty || awayTeamDoc.empty) {
        throw new Error('Teams not found');
      }

      const homeTeam = homeTeamDoc.docs[0].data();
      const awayTeam = awayTeamDoc.docs[0].data();

      console.log('Getting players for both teams...');
      
      // Get players for both teams
      const [homePlayersQuery, awayPlayersQuery] = await Promise.all([
        getDocs(query(collection(db, 'players'), where('team', '==', fixture.homeTeam))),
        getDocs(query(collection(db, 'players'), where('team', '==', fixture.awayTeam)))
      ]);

      const homePlayers = homePlayersQuery.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const awayPlayers = awayPlayersQuery.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      console.log(`Home players found: ${homePlayers.length}`);
      console.log(`Away players found: ${awayPlayers.length}`);

      if (homePlayers.length < 2 || awayPlayers.length < 2) {
        throw new Error(`Each team needs at least 2 players. Found: ${homePlayers.length} home, ${awayPlayers.length} away`);
      }

      console.log('Creating 4 match documents...');
      
      // Create 4 matches: Player1 vs Player1, Player1 vs Player2, Player2 vs Player1, Player2 vs Player2
      const matches = [];
      for (let i = 0; i < 2; i++) {
        for (let j = 0; j < 2; j++) {
          const matchData = {
            fixtureId: fixture.id,
            leagueId: fixture.leagueId,
            isPlayoff: !!fixture.isPlayoff,
            playoffRound: fixture.playoffRound || null,
            homeTeam: fixture.homeTeam,
            awayTeam: fixture.awayTeam,
            homePlayer: homePlayers[i],
            awayPlayer: awayPlayers[j],
            status: 'scheduled',
            matchNumber: i * 2 + j + 1,
            date: fixture.date,
            time: fixture.time,
            court: fixture.court,
            sets: [
              { set: 1, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, completed: false },
              { set: 2, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, completed: false },
              { set: 3, homeScore: 0, awayScore: 0, homeGames: 0, awayGames: 0, completed: false }
            ],
            playerStats: {
              home: { aces: 0, unforcedErrors: 0, kickSmashes: 0, akhlaaqRating: 3 },
              away: { aces: 0, unforcedErrors: 0, kickSmashes: 0, akhlaaqRating: 3 }
            },
            createdAt: new Date().toISOString()
          };
          matches.push(matchData);
          console.log(`Match ${i * 2 + j + 1}: ${homePlayers[i].name} vs ${awayPlayers[j].name}`);
        }
      }

      console.log('Saving matches to Firestore...');
      
      // Save all matches
      const savePromises = matches.map(match => addDoc(collection(db, 'matches'), match));
      await Promise.all(savePromises);

      console.log('✅ All 4 matches created successfully!');
      setSuccess('4 matches created successfully!');
      
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => {
        setShow4MatchDialog(false);
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('❌ Error creating 4 matches:', err);
      console.error('Error details:', err.message, err.stack);
      setError('Failed to create 4 matches: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateTBDTeams = async () => {
    if (!selectedHomeTeam || !selectedAwayTeam) {
      setError('Please select both teams');
      return;
    }

    if (selectedHomeTeam === selectedAwayTeam) {
      setError('Please select different teams');
      return;
    }

    console.log('📝 UPDATING TBD TEAMS');
    console.log('Home Team:', selectedHomeTeam);
    console.log('Away Team:', selectedAwayTeam);
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const homeTeam = availableTeams.find(t => t.id === selectedHomeTeam);
      const awayTeam = availableTeams.find(t => t.id === selectedAwayTeam);

      if (!homeTeam || !awayTeam) {
        throw new Error('Selected teams not found');
      }

      // Get ALL fixtures for this playoff round (all 4 matches)
      const fixturesQuery = query(
        collection(db, 'fixtures'),
        where('leagueId', '==', fixture.leagueId),
        where('isPlayoff', '==', true),
        where('playoffRound', '==', fixture.playoffRound)
      );
      
      const fixturesSnapshot = await getDocs(fixturesQuery);
      console.log(`Found ${fixturesSnapshot.docs.length} fixtures to update`);

      // Update all 4 fixtures in this playoff round
      const updatePromises = fixturesSnapshot.docs.map(doc => 
        updateDoc(doc.ref, {
          homeTeam: homeTeam.name,
          homeTeamId: homeTeam.id,
          awayTeam: awayTeam.name,
          awayTeamId: awayTeam.id,
          updatedAt: new Date().toISOString()
        })
      );

      await Promise.all(updatePromises);

      console.log('✅ All fixtures updated successfully!');
      setSuccess(`Teams updated: ${homeTeam.name} vs ${awayTeam.name} (${fixturesSnapshot.docs.length} matches)`);
      
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => {
        setShowTeamSelectionDialog(false);
        setSelectedHomeTeam('');
        setSelectedAwayTeam('');
        setSuccess('');
        onClose();
      }, 2000);
    } catch (err) {
      console.error('❌ Error updating teams:', err);
      setError('Failed to update teams: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyWalkover = async () => {
    if (!walkoverWinner) {
      setError('Please select a winner');
      return;
    }

    console.log('🏳️ APPLYING WALKOVER');
    console.log('Winner:', walkoverWinner);
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const isHomeWinner = walkoverWinner === 'home';
      const winnerTeam = isHomeWinner ? fixture.homeTeam : fixture.awayTeam;
      const loserTeam = isHomeWinner ? fixture.awayTeam : fixture.homeTeam;

      // Update fixture with walkover - 3-0 scoreline, no player stats affected
      await updateDoc(doc(db, 'fixtures', fixture.id), {
        status: 'completed',
        isWalkover: true,
        walkoverWinner: winnerTeam,
        walkoverReason: 'Team forfeit',
        sets: [
          { 
            set: 1, 
            homeScore: isHomeWinner ? 6 : 0, 
            awayScore: isHomeWinner ? 0 : 6, 
            completed: true,
            isWalkover: true 
          },
          { 
            set: 2, 
            homeScore: isHomeWinner ? 6 : 0, 
            awayScore: isHomeWinner ? 0 : 6, 
            completed: true,
            isWalkover: true 
          },
          { 
            set: 3, 
            homeScore: isHomeWinner ? 6 : 0, 
            awayScore: isHomeWinner ? 0 : 6, 
            completed: true,
            isWalkover: true 
          }
        ],
        homeSetsWon: isHomeWinner ? 3 : 0,
        awaySetsWon: isHomeWinner ? 0 : 3,
        winner: winnerTeam,
        finalScore: isHomeWinner ? '3-0' : '0-3',
        completedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      console.log('✅ Walkover applied successfully!');
      setSuccess(`Walkover applied: ${winnerTeam} wins 3-0`);
      
      if (onUpdate) {
        onUpdate();
      }
      
      setTimeout(() => {
        setShowWalkoverDialog(false);
        setSuccess('');
        onClose();
      }, 2000);
    } catch (err) {
      console.error('❌ Error applying walkover:', err);
      setError('Failed to apply walkover: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Main Modal Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
            borderRadius: '1.5rem',
            padding: '2rem',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid rgba(28, 86, 61, 0.1)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
        >
          {/* Modal Header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '2rem',
            paddingBottom: '1rem',
            borderBottom: '2px solid #E5E7EB'
          }}>
            <div>
              <h2 style={{ 
                fontSize: '1.75rem', 
                fontWeight: 'bold', 
                color: '#1C563D',
                marginBottom: '0.5rem'
              }}>
                Manage Fixture
              </h2>
              <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: 0 }}>
                {fixture.homeTeam} vs {fixture.awayTeam}
              </p>
              <p style={{ fontSize: '0.75rem', color: '#9CA3AF', margin: '0.25rem 0 0 0' }}>
                {new Date(fixture.date).toLocaleDateString()} • {fixture.time} • {fixture.court}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '0.5rem',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#F3F4F6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <X size={20} style={{ color: '#6B7280' }} />
            </button>
          </div>

          {/* Fixture Management Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                console.log('🔴 Reset Fixture button clicked!');
                console.log('Current showResetDialog state:', showResetDialog);
                setShowResetDialog(true);
                console.log('Set showResetDialog to TRUE');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1.25rem',
                background: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
                border: '2px solid #EF4444',
                borderRadius: '1rem',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem',
                color: '#DC2626',
                transition: 'all 0.2s ease',
                textAlign: 'left'
              }}
            >
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <RotateCcw size={24} style={{ color: 'white' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Reset Fixture</div>
                <div style={{ fontSize: '0.75rem', color: '#991B1B', fontWeight: '500' }}>
                  Clear all scores and match data
                </div>
              </div>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                console.log('Reschedule button clicked, opening dialog...');
                setShowRescheduleDialog(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1.25rem',
                background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                border: '2px solid #3B82F6',
                borderRadius: '1rem',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem',
                color: '#1E40AF',
                transition: 'all 0.2s ease',
                textAlign: 'left'
              }}
            >
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Calendar size={24} style={{ color: 'white' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Reschedule Fixture</div>
                <div style={{ fontSize: '0.75rem', color: '#1E3A8A', fontWeight: '500' }}>
                  Change date, time, and court
                </div>
              </div>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShow4MatchDialog(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1.25rem',
                background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)',
                border: '2px solid #10B981',
                borderRadius: '1rem',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem',
                color: '#065F46',
                transition: 'all 0.2s ease',
                textAlign: 'left'
              }}
            >
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <RefreshCw size={24} style={{ color: 'white' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Create 4 Matches</div>
                <div style={{ fontSize: '0.75rem', color: '#047857', fontWeight: '500' }}>
                  Generate all player combinations
                </div>
              </div>
            </motion.button>

            {/* Set Teams Button - For TBD Playoff Fixtures */}
            {fixture.isPlayoff && (!fixture.homeTeamId || !fixture.awayTeamId || fixture.homeTeam?.includes('TBD')) && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowTeamSelectionDialog(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1.25rem',
                  background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
                  border: '2px solid #3B82F6',
                  borderRadius: '1rem',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  color: '#1E40AF',
                  transition: 'all 0.2s ease',
                  textAlign: 'left'
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  borderRadius: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Users size={24} style={{ color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    Set Teams
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#1E3A8A', fontWeight: '500' }}>
                    Assign teams to TBD playoff fixture
                  </div>
                </div>
              </motion.button>
            )}

            {/* Walkover Button - Super Admin Only */}
            {isSuperAdmin() && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowWalkoverDialog(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1.25rem',
                  background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                  border: '2px solid #F59E0B',
                  borderRadius: '1rem',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  color: '#92400E',
                  transition: 'all 0.2s ease',
                  textAlign: 'left'
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                  borderRadius: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Flag size={24} style={{ color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    Apply Walkover
                    <span style={{
                      fontSize: '0.625rem',
                      padding: '0.125rem 0.5rem',
                      background: '#DC2626',
                      color: 'white',
                      borderRadius: '0.25rem',
                      fontWeight: '700',
                      textTransform: 'uppercase'
                    }}>
                      Super Admin
                    </span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#78350F', fontWeight: '500' }}>
                    Award 3-0 win for team forfeit
                  </div>
                </div>
              </motion.button>
            )}
          </div>
        </motion.div>
      </div>

      {/* Reset Dialog */}
      <AnimatePresence>
        {showResetDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowResetDialog(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '2rem',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AlertTriangle size={24} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>
                    Reset Fixture
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: 0 }}>
                    This will reset all scores and match data
                  </p>
                </div>
              </div>

              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#DC2626', margin: 0 }}>
                  <strong>Warning:</strong> This action will:
                </p>
                <ul style={{ fontSize: '0.875rem', color: '#DC2626', margin: '0.5rem 0 0 1rem' }}>
                  <li>Reset fixture status to 'scheduled'</li>
                  <li>Clear all scores and player stats</li>
                  <li>Delete match records</li>
                  <li>Remove winner information</li>
                </ul>
              </div>

              {error && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#DC2626',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem'
                }}>
                  {error}
                </div>
              )}

              {success && (
                <div style={{
                  background: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  color: '#059669',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <CheckCircle size={16} />
                  {success}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowResetDialog(false)}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={resetFixture}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: loading ? '#9CA3AF' : 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {loading ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  {loading ? 'Resetting...' : 'Reset Fixture'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reschedule Dialog */}
      <AnimatePresence>
        {showRescheduleDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                console.log('Closing reschedule dialog...');
                setShowRescheduleDialog(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '2rem',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                zIndex: 10001
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Calendar size={24} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>
                    Reschedule Fixture
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: 0 }}>
                    Update date, time, and court information
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#10B981', margin: '0.25rem 0 0 0', fontWeight: '600' }}>
                    ✅ Modal is working!
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Date
                  </label>
                  <input
                    type="date"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #E5E7EB',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Time
                  </label>
                  <input
                    type="time"
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #E5E7EB',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Court
                  </label>
                  <input
                    type="text"
                    value={newCourt}
                    onChange={(e) => setNewCourt(e.target.value)}
                    placeholder="Court 1, Court 2, etc."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #E5E7EB',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              {error && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#DC2626',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem'
                }}>
                  {error}
                </div>
              )}

              {success && (
                <div style={{
                  background: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  color: '#059669',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <CheckCircle size={16} />
                  {success}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowRescheduleDialog(false)}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={rescheduleFixture}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: loading ? '#9CA3AF' : 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {loading ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                  {loading ? 'Saving...' : 'Reschedule'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 4 Matches Dialog */}
      <AnimatePresence>
        {show4MatchDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShow4MatchDialog(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '2rem',
                maxWidth: '600px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <RefreshCw size={24} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>
                    Create 4 Matches
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: 0 }}>
                    Generate all player vs player combinations
                  </p>
                </div>
              </div>

              <div style={{
                background: '#F0FDF4',
                border: '1px solid #BBF7D0',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#059669', margin: 0, fontWeight: '600' }}>
                  Game Day Rule: 4 Matches Per Fixture
                </p>
                <p style={{ fontSize: '0.875rem', color: '#065F46', margin: '0.5rem 0 0 0' }}>
                  This will create 4 individual matches where each player from Team 1 plays against each player from Team 2.
                </p>
                <ul style={{ fontSize: '0.875rem', color: '#065F46', margin: '0.5rem 0 0 1rem' }}>
                  <li>Match 1: Player 1 (Home) vs Player 1 (Away)</li>
                  <li>Match 2: Player 1 (Home) vs Player 2 (Away)</li>
                  <li>Match 3: Player 2 (Home) vs Player 1 (Away)</li>
                  <li>Match 4: Player 2 (Home) vs Player 2 (Away)</li>
                </ul>
              </div>

              {error && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#DC2626',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem'
                }}>
                  {error}
                </div>
              )}

              {success && (
                <div style={{
                  background: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  color: '#059669',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <CheckCircle size={16} />
                  {success}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShow4MatchDialog(false)}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={create4Matches}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: loading ? '#9CA3AF' : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {loading ? <RefreshCw size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                  {loading ? 'Creating...' : 'Create 4 Matches'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Team Selection Dialog */}
      <AnimatePresence>
        {showTeamSelectionDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowTeamSelectionDialog(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '2rem',
                maxWidth: '600px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Users size={24} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>
                    Set Teams for {fixture.playoffRound}
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: 0 }}>
                    Select teams for all 4 matches in this playoff round
                  </p>
                </div>
              </div>

              <div style={{
                background: '#DBEAFE',
                border: '1px solid #BFDBFE',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#1E40AF', margin: 0, fontWeight: '600' }}>
                  ℹ️ This will update all 4 matches for {fixture.playoffRound}
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Home Team:
                  </label>
                  <select
                    value={selectedHomeTeam}
                    onChange={(e) => setSelectedHomeTeam(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #E5E7EB',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      background: 'white'
                    }}
                  >
                    <option value="">Select home team...</option>
                    {availableTeams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Away Team:
                  </label>
                  <select
                    value={selectedAwayTeam}
                    onChange={(e) => setSelectedAwayTeam(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #E5E7EB',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      background: 'white'
                    }}
                  >
                    <option value="">Select away team...</option>
                    {availableTeams.map(team => (
                      <option key={team.id} value={team.id} disabled={team.id === selectedHomeTeam}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {error && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#DC2626',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem'
                }}>
                  {error}
                </div>
              )}

              {success && (
                <div style={{
                  background: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  color: '#059669',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <CheckCircle size={16} />
                  {success}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowTeamSelectionDialog(false);
                    setSelectedHomeTeam('');
                    setSelectedAwayTeam('');
                  }}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={updateTBDTeams}
                  disabled={loading || !selectedHomeTeam || !selectedAwayTeam}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: (loading || !selectedHomeTeam || !selectedAwayTeam) ? '#9CA3AF' : 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: (loading || !selectedHomeTeam || !selectedAwayTeam) ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {loading ? <RefreshCw size={16} className="animate-spin" /> : <Users size={16} />}
                  {loading ? 'Updating...' : 'Set Teams'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Walkover Dialog */}
      <AnimatePresence>
        {showWalkoverDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowWalkoverDialog(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '2rem',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Flag size={24} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>
                    Apply Walkover
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: 0 }}>
                    Award 3-0 win due to team forfeit
                  </p>
                </div>
              </div>

              <div style={{
                background: '#FEF3C7',
                border: '1px solid #FDE68A',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#92400E', margin: 0, fontWeight: '600' }}>
                  ⚠️ Walkover Rules:
                </p>
                <ul style={{ fontSize: '0.875rem', color: '#92400E', margin: '0.5rem 0 0 1rem' }}>
                  <li>Winner receives 3-0 victory (3 sets)</li>
                  <li>No player stats will be affected</li>
                  <li>Fixture will be marked as completed</li>
                  <li>Cannot be undone (use Reset Fixture to reverse)</li>
                </ul>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
                  Select Winner:
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '1rem',
                    border: walkoverWinner === 'home' ? '2px solid #10B981' : '2px solid #E5E7EB',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    background: walkoverWinner === 'home' ? '#F0FDF4' : 'white',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="walkoverWinner"
                      value="home"
                      checked={walkoverWinner === 'home'}
                      onChange={(e) => setWalkoverWinner(e.target.value)}
                      style={{ marginRight: '0.75rem', width: '20px', height: '20px' }}
                    />
                    <span style={{ fontWeight: '600', color: '#1F2937' }}>
                      {fixture.homeTeam}
                    </span>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '1rem',
                    border: walkoverWinner === 'away' ? '2px solid #10B981' : '2px solid #E5E7EB',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    background: walkoverWinner === 'away' ? '#F0FDF4' : 'white',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="walkoverWinner"
                      value="away"
                      checked={walkoverWinner === 'away'}
                      onChange={(e) => setWalkoverWinner(e.target.value)}
                      style={{ marginRight: '0.75rem', width: '20px', height: '20px' }}
                    />
                    <span style={{ fontWeight: '600', color: '#1F2937' }}>
                      {fixture.awayTeam}
                    </span>
                  </label>
                </div>
              </div>

              {error && (
                <div style={{
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#DC2626',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem'
                }}>
                  {error}
                </div>
              )}

              {success && (
                <div style={{
                  background: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  color: '#059669',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <CheckCircle size={16} />
                  {success}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowWalkoverDialog(false);
                    setWalkoverWinner('');
                  }}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={applyWalkover}
                  disabled={loading || !walkoverWinner}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: (loading || !walkoverWinner) ? '#9CA3AF' : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: (loading || !walkoverWinner) ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {loading ? <RefreshCw size={16} className="animate-spin" /> : <Flag size={16} />}
                  {loading ? 'Applying...' : 'Apply Walkover'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FixtureManager;
